/* eslint-disable max-lines */
import type { SupportedPlatforms } from '@/api/rankingExplainer.types';
import { SupportedPlatform } from '@/api/similarListings';
import {
  InsertionSource,
  SearchControlSortBy,
  mappedTrackingSorting,
  mappedTrackingSources,
  mappedTrackingStatuses,
} from '@/models/insertionsParameters';
import type { ListingInsightsTabId } from '@/models/listingInsightsTabIds';
import {
  ConversionSource,
  EventAction,
  EventLabel,
  FeatureGroup,
  GTMPageFeatureId,
  InsightsHubTrackingAction,
  trackEvent,
  type EditSearchQueryTrackingDimensions,
  type GTMEventCallback,
  type PageTrackingParameters,
} from '@/services/tracking/GtmTrackingService';
import {
  buildTabNavigationLabel,
  mapListingInsightsTabIdToTrackingSlug,
} from '@/services/tracking/listingInsightsTabTracking';
import { getNbaTrackingAction } from '@/services/tracking/nbaTrackingActions';
import { mapRankingSnapshotPlatformToTrackingLabel } from '@/services/tracking/rankingSnapshotPlatformTracking';
import { useUserStore } from '@/stores/user/useUserStore';
import {
  InsertionStatus,
  LandingFormStep,
  UserGroup,
  UserTypeLogin,
  type AuthorizedUser,
  type UserId,
} from '@/types';
import { NbaActions } from '@/types-nba';
import { Listing, OfferType, RankType } from '@smg-real-estate/hg-rets-schema';
import { getLifetimeTopListingPrices } from '@smg-real-estate/toplisting-prices';

export { getNbaTrackingAction } from '@/services/tracking/nbaTrackingActions';

// TODO: This might not be ok
export const getUserTypeFrom = (): UserGroup => {
  const groups = useUserStore().groups;
  if (groups.includes(UserGroup.AGENCY)) {
    return UserGroup.AGENCY;
  }
  return groups.includes(UserGroup.CUSTOMER_SERVICE_AGENT)
    ? UserGroup.CUSTOMER_SERVICE_AGENT
    : UserGroup.PRIVATE;
};

export function trackNewInsertionFormSubmit(listing: Listing): void {
  trackEvent({
    tech_featureId: GTMPageFeatureId.LANDING_PAGE_SUBMIT,
    event: 'transition_button',
    interaction_actor: 'user',
    interaction_containerDetail: 'input_start',
    interaction_element: undefined,
    interaction_error: undefined,
    interaction_mode: undefined,
    interaction_sysFeedback: 'ok',
    lead_type: undefined,
    listing_categories: listing.categories?.toString(),
    listing_country: listing.address?.country,
    listing_offerType: listing.offerType,
    listing_zip: listing.address?.postalCode,
    listing_canton: listing.address?.region,
  });
}

export function trackPublishInsertionFormSubmit(listing: Listing): void {
  const baseTrackingParams = {
    event: 'purchase',
    h_event_category_006: 'insertion_interaction',
    h_event_action_007: 'BC_transaction',
    h_event_label_008: listing.externalIds?.propertyReferenceId,
    non_interaction: false,
    h_transaction_id_042: listing.externalIds?.propertyReferenceId,
    h_purchase_list_items_all_173: listing.externalIds?.propertyReferenceId,
    listing_categories: listing.categories?.toString(),
    listing_country: listing.address?.country,
    listing_offerType: listing.offerType,
    listing_zip: listing.address?.postalCode,
    listing_canton: listing.address?.region,
  };

  let lifetimeToplistingParams = {};
  if (listing.valueAddedServices?.isLifetimeToplisting) {
    const lifetimeToplistingPrice =
      listing.offerType === OfferType.Buy
        ? getLifetimeTopListingPrices().priceLifetimeTopListingBuyChf
        : getLifetimeTopListingPrices().priceLifetimeTopListingRentChf;

    lifetimeToplistingParams = {
      h_transaction_total_017: lifetimeToplistingPrice,
      h_transaction_type_063: 'Create',
      h_product_matching_id_038: 'lifetime_tl',
    };
  }

  trackEvent({
    ...baseTrackingParams,
    ...lifetimeToplistingParams,
  });
}

export function trackLandingPageWizardTransitions(step: LandingFormStep, buttonText: string): void {
  const pageParams = {
    event: 'insertion_interaction',
    client_sns_subscriptionId: undefined,
    interaction_actor: undefined,
    interaction_containerDetail: mapLandingFormStepToContainerDetail(step),
    interaction_containerName: buttonText,
    interaction_element: undefined,
    interaction_error: undefined,
    interaction_goal: 'goToNextStep',
    interaction_mode: undefined,
    interaction_sysFeedback: undefined,
    lead_type: undefined,
    tech_featureId: GTMPageFeatureId.LANDING_PAGE_MOBILE_TRANSITION,
    tech_leadId: undefined,
  };

  trackEvent(pageParams);
}

export function trackAiGenerate({
  action,
  listingId,
  agencyId,
}: {
  action: string;
  listingId?: string;
  agencyId?: string;
}): void {
  const pageParams = {
    event: 'insertion_interaction',
    h_event_category_006: 'insertion_interaction',
    h_event_action_007: action,
    ...(listingId !== undefined ? { h_listing_advertisement_id_120: listingId } : {}),
    ...(agencyId !== undefined ? { h_client_agency_id_033: agencyId } : {}),
  };

  trackEvent(pageParams);
}

export function trackPrefillConfirmed(): void {
  const pageParams = {
    event: 'insertion_interaction',
    h_event_category_006: 'insertion_interaction',
    h_event_action_007: 'continue_automated_insertion',
    h_event_label_008: undefined,
  };

  trackEvent(pageParams);
}

export function trackContactDropdownOpened(): void {
  const pageParams = {
    event: 'insertion_interaction',
    h_event_category_006: 'insertion_interaction',
    h_event_action_007: 'open_contact_dropdown',
    h_event_label_008: undefined,
  };

  trackEvent(pageParams);
}

export function trackStoredContactSelected(): void {
  const pageParams = {
    event: 'insertion_interaction',
    h_event_category_006: 'insertion_interaction',
    h_event_action_007: 'select_stored_contact',
    h_event_label_008: undefined,
  };

  trackEvent(pageParams);
}

export function trackPrefillDeclined(): void {
  const pageParams = {
    event: 'insertion_interaction',
    h_event_category_006: 'insertion_interaction',
    h_event_action_007: 'decline_automated_insertion',
    h_event_label_008: undefined,
  };

  trackEvent(pageParams);
}

export function trackShowPrefill(): void {
  const pageParams = {
    event: 'feature_displayed',
    h_event_category_006: 'feature_displayed',
    h_event_action_007: 'display_automated_insertion',
    h_event_label_008: undefined,
  };

  trackEvent(pageParams);
}

export function getAgencyId(user: AuthorizedUser | null): string | undefined {
  if (user?.groups?.includes(UserGroup.AGENCY)) {
    return user?.userId;
  }
  return user?.logonId;
}

export function getExternalHrefEventCallback(href: string): GTMEventCallback {
  const url = new URL(href, window.location.origin);
  return () => window.location.assign(url.href);
}

export function mapRankTypeToContainerDetail(rankType?: RankType): string | undefined {
  switch (rankType) {
    case RankType.Top:
      return 'toplistingPlus';
    case RankType.Premium:
      return 'premiumListing';
    default:
      return undefined;
  }
}

export function mapLandingFormStepToContainerDetail(step: LandingFormStep): string | undefined {
  switch (step) {
    case LandingFormStep.CUSTOMER_SEGMENT:
      return 'advertiserType';
    case LandingFormStep.OFFER_TYPE:
      return 'listingOfferType';
    case LandingFormStep.CATEGORY:
      return 'listingCategory';
    case LandingFormStep.TYPE:
      return 'listingType';
    default:
      return undefined;
  }
}

export function trackClickedElement({
  action,
  label,
  listingId,
  agencyId,
  userId,
  objectReferenceId,
  extraInformation,
  featureGroup,
  experimentVariantArray,
}: {
  action: string;
  label?: string;
  listingId?: string;
  agencyId?: string;
  userId?: string;
  objectReferenceId?: string;
  extraInformation?: string;
  featureGroup?: string;
  experimentVariantArray?: string;
}): void {
  const eventParams: PageTrackingParameters = {
    event: 'ubm_interaction',
    h_event_category_006: 'ubm_interaction',
    h_event_action_007: action,
    ...(featureGroup !== undefined ? { h_feature_group_001: featureGroup } : {}),
    ...(label !== undefined ? { h_event_label_008: label } : {}),
    ...(listingId !== undefined ? { h_listing_advertisement_id_120: listingId } : {}),
    ...(agencyId !== undefined ? { h_client_agency_id_033: agencyId } : {}),
    ...(userId !== undefined ? { h_client_login_user_id_036: userId } : {}),
    ...(objectReferenceId !== undefined ? { h_listing_object_ref_id_123: objectReferenceId } : {}),
    ...(extraInformation !== undefined ? { h_extra_information_144: extraInformation } : {}),
    ...(experimentVariantArray !== undefined
      ? { h_experiment_variant_array_048: experimentVariantArray }
      : {}),
  };

  trackEvent(eventParams);
}

const TOP_LISTING_BOOKING_FEATURE_GROUP = 'listing_tl_booking';

export function trackToplistingModalDisplayed(
  listingId: string | undefined,
  conversionSource: ConversionSource,
  objectReferenceId?: string,
  experimentVariantArray?: string,
): void {
  trackEvent({
    event: 'feature_displayed',
    h_event_category_006: 'feature_displayed',
    h_event_action_007: EventAction.DISPLAY_TOPLISTING_MODAL,
    h_feature_group_001: TOP_LISTING_BOOKING_FEATURE_GROUP,
    h_conversion_source_061: conversionSource,
    ...(listingId !== undefined ? { h_listing_advertisement_id_120: listingId } : {}),
    ...(objectReferenceId !== undefined ? { h_listing_object_ref_id_123: objectReferenceId } : {}),
    ...(experimentVariantArray !== undefined
      ? { h_experiment_variant_array_048: experimentVariantArray }
      : {}),
  });
}

export function trackToplistingDeactivationModalDisplayed(listingId?: string): void {
  trackEvent({
    event: 'feature_displayed',
    h_event_category_006: 'feature_displayed',
    h_event_action_007: EventAction.DISPLAY_DEACTIVATE_TOPLISTING_MODAL,
    h_event_label_008: undefined,
    ...(listingId !== undefined ? { h_listing_advertisement_id_120: listingId } : {}),
  });
}

export function trackToplistingInteraction({
  action,
  label,
  listingId,
  conversionSource,
  objectReferenceId,
  experimentVariantArray,
}: {
  action: EventAction;
  label?: string;
  listingId?: string;
  conversionSource: ConversionSource;
  objectReferenceId?: string;
  experimentVariantArray?: string;
}): void {
  trackEvent({
    event: 'ubm_interaction',
    h_event_category_006: 'ubm_interaction',
    h_event_action_007: action,
    h_feature_group_001: TOP_LISTING_BOOKING_FEATURE_GROUP,
    h_conversion_source_061: conversionSource,
    ...(label !== undefined ? { h_event_label_008: label } : {}),
    ...(listingId !== undefined ? { h_listing_advertisement_id_120: listingId } : {}),
    ...(objectReferenceId !== undefined ? { h_listing_object_ref_id_123: objectReferenceId } : {}),
    ...(experimentVariantArray !== undefined
      ? { h_experiment_variant_array_048: experimentVariantArray }
      : {}),
  });
}

export function trackToplistingBooking({
  listingId,
  conversionSource,
  packageLabel,
  durationInDays,
  pricePerDay,
  totalCost,
  objectReferenceId,
  experimentVariantArray,
}: {
  listingId?: string;
  conversionSource: ConversionSource;
  packageLabel: string;
  durationInDays?: number | null;
  pricePerDay?: number | string;
  totalCost?: number;
  objectReferenceId?: string;
  experimentVariantArray?: string;
}): void {
  trackEvent({
    event: 'ubm_interaction',
    h_event_category_006: 'ubm_interaction',
    h_event_action_007: EventAction.BOOK_TOPLISTING,
    h_event_label_008: EventLabel.IN_MODAL,
    h_feature_group_001: TOP_LISTING_BOOKING_FEATURE_GROUP,
    h_conversion_source_061: conversionSource,
    h_transaction_type_063: 'ubm_top_listing',
    h_purchase_list_items_all_173: packageLabel,
    ...(listingId !== undefined ? { h_listing_advertisement_id_120: listingId } : {}),
    ...(objectReferenceId !== undefined ? { h_listing_object_ref_id_123: objectReferenceId } : {}),
    ...(experimentVariantArray !== undefined
      ? { h_experiment_variant_array_048: experimentVariantArray }
      : {}),
    ...(durationInDays !== undefined && durationInDays !== null
      ? { h_funnel_step_input_141: durationInDays }
      : {}),
    ...(pricePerDay !== undefined ? { h_extra_information_144: pricePerDay } : {}),
    ...(totalCost !== undefined ? { h_transaction_total_017: totalCost } : {}),
  });
}

export function trackFilterInteraction({
  action,
  filterLabel,
}: {
  action: 'apply_filter' | 'remove_filter';
  filterLabel: string;
}): void {
  const eventParams: PageTrackingParameters = {
    event: 'ubm_interaction',
    h_event_category_006: 'ubm_interaction',
    h_event_action_007: action,
    h_event_label_008: filterLabel,
  };

  trackEvent(eventParams);
}

export function trackAuthenticationAfterRedirect(userId: UserId, firstTimeLogin?: boolean): void {
  trackEvent({
    event: 'authentication',
    h_event_category_006: 'authentication',
    h_event_action_007: firstTimeLogin ? 'auth_sign_up' : 'auth_login',
    h_event_label_008: 'smg_re_web',
    h_client_login_user_id_036: userId,
    h_login_status_035: 'loggedin',
    h_login_location_049: 'ubm',
    h_client_login_provider_034: 'auth0',
  });
}

const parseValuesToTracking = (arr: string[] | undefined): string | undefined =>
  arr?.length ? arr.join('&').toLowerCase() : undefined;

export function trackPageLoadEvent({
  agencyId,
  language,
  listingId,
  pageSubType,
  userId,
  userTypeLogin,
  objectReferenceId,
  searchSortedDetails,
  searchTermGeo,
  listingStatus,
  searchOfferType,
  searchFilterCategory,
  listingSource,
  listingVas,
  listingCompletenessScore,
  conversionSource,
}: {
  agencyId: string;
  language: string;
  listingId?: string;
  pageSubType: string;
  userId: string;
  userTypeLogin: UserTypeLogin;
  objectReferenceId?: string;
  searchSortedDetails?: SearchControlSortBy;
  searchTermGeo?: string[];
  listingStatus?: InsertionStatus[];
  searchOfferType?: string[];
  searchFilterCategory?: string[];
  listingSource?: InsertionSource[];
  listingVas?: string[];
  listingCompletenessScore?: string[];
  conversionSource?: ConversionSource;
}): void {
  const pageParams: PageTrackingParameters = {
    event: 'virtual_pageview',
    h_page_type_110: 'ubm',
    h_page_subtype_112: pageSubType,
    ...(listingId !== undefined ? { h_listing_advertisement_id_120: listingId } : {}),
    h_content_language_116: language,
    h_client_login_user_type_037: userTypeLogin,
    h_client_login_user_id_036: userId,
    h_client_agency_id_033: agencyId,
    ...(objectReferenceId !== undefined ? { h_listing_object_ref_id_123: objectReferenceId } : {}),
    h_search_sorted_details_073: searchSortedDetails
      ? mappedTrackingSorting[searchSortedDetails]
      : undefined,
    h_search_term_geo_080: searchTermGeo?.join('_').replaceAll(' ', '_') || undefined,
    h_listing_status_121: parseValuesToTracking(
      Array.from(new Set(listingStatus?.map((status) => mappedTrackingStatuses[status]))),
    ),
    h_search_offer_type_083: parseValuesToTracking(searchOfferType),
    h_search_filter_category_075: parseValuesToTracking(searchFilterCategory),
    h_listing_source_153: parseValuesToTracking(
      listingSource?.map((source) => mappedTrackingSources[source]),
    ),
    h_listing_vas_156: parseValuesToTracking(listingVas),
    h_listing_completeness_score_127: parseValuesToTracking(listingCompletenessScore),
    ...(conversionSource ? { h_conversion_source_061: conversionSource } : {}),
  };

  trackEvent(pageParams);
}

export function trackLifetimeTopListingToggle(isAdding: boolean): void {
  trackEvent({
    event: 'insertion_interaction',
    h_event_category_006: 'insertion_interaction',
    h_event_action_007: isAdding ? 'add_lifetime_tl' : 'remove_lifetime_tl',
    h_event_label_008: undefined,
  });
}

export function trackFileUpload(action: 'upload_image' | 'upload_document'): void {
  const pageParams = {
    event: 'insertion_interaction',
    h_event_category_006: 'insertion_interaction',
    h_event_action_007: action,
    h_event_label_008: undefined,
  };

  trackEvent(pageParams);
}

/** TFP-3382: NBA action click on Insights Hub */
export function trackNbaActionClick(action: NbaActions, listingId?: string): void {
  const trackingParams: PageTrackingParameters = {
    event: 'ubm_interaction',
    h_feature_group_001: FeatureGroup.INSIGHTS_HUB,
    h_event_action_007: getNbaTrackingAction(action),
    h_event_label_008: EventLabel.NEXT_BEST_ACTION,
    ...(listingId && { h_listing_advertisement_id_120: listingId }),
  };
  trackEvent(trackingParams);
}

export function trackNbaShowAllSuggestions(): void {
  trackEvent({
    event: 'ubm_interaction',
    h_feature_group_001: FeatureGroup.INSIGHTS_HUB,
    h_event_action_007: EventAction.SHOW_ALL,
    h_event_label_008: EventLabel.NEXT_BEST_ACTION,
  });
}

export function trackListingInsightsEventUpdatesToggle(
  isEnabled: boolean,
  listingId?: string,
): void {
  trackEvent({
    event: 'ubm_interaction',
    h_event_action_007: EventAction.LISTING_INSIGHTS,
    h_event_label_008: isEnabled
      ? EventLabel.ACTIVATE_EVENTS_UPDATES
      : EventLabel.DEACTIVATE_EVENTS_UPDATES,
    ...(listingId !== undefined ? { h_listing_advertisement_id_120: listingId } : {}),
    h_feature_group_001: FeatureGroup.INSIGHTS_HUB,
  });
}

export function trackListingInsightsEventUpdatesInfo(listingId?: string): void {
  trackEvent({
    event: 'ubm_interaction',
    h_event_action_007: EventAction.LISTING_INSIGHTS,
    h_event_label_008: EventLabel.VIEW_EVENTS_UPDATES_INFO,
    ...(listingId !== undefined ? { h_listing_advertisement_id_120: listingId } : {}),
    h_feature_group_001: FeatureGroup.INSIGHTS_HUB,
  });
}

export function trackSimilarListingsInfo(listingId?: string): void {
  trackEvent({
    event: 'ubm_interaction',
    h_event_action_007: EventAction.LISTING_INSIGHTS,
    h_event_label_008: EventLabel.VIEW_SIMILAR_LISTING_INFO,
    ...(listingId !== undefined ? { h_listing_advertisement_id_120: listingId } : {}),
    h_feature_group_001: FeatureGroup.INSIGHTS_HUB,
  });
}

const SIMILAR_LISTING_PLATFORM_LABELS: Partial<Record<SupportedPlatform, EventLabel>> = {
  [SupportedPlatform.HOMEGATE]: EventLabel.VISIT_HOMEGATE_SIMILAR_LISTING,
  [SupportedPlatform.IMMOSCOUT24]: EventLabel.VISIT_IMMOSCOUT24_SIMILAR_LISTING,
};

export function trackSimilarListingPlatformClick(platform: SupportedPlatform): void {
  const label = SIMILAR_LISTING_PLATFORM_LABELS[platform];
  if (!label) {
    return;
  }

  trackEvent({
    event: 'ubm_interaction',
    h_feature_group_001: FeatureGroup.INSIGHTS_HUB,
    h_event_action_007: EventAction.SIMILAR_LISTING,
    h_event_label_008: label,
  });
}

export function trackTopListingCostReportView(listingId?: string): void {
  trackEvent({
    event: 'ubm_interaction',
    h_event_action_007: EventAction.LISTING_DETAILS,
    h_event_label_008: EventLabel.VIEW_TOP_LISTING_COST_REPORT,
    ...(listingId !== undefined ? { h_listing_advertisement_id_120: listingId } : {}),
    h_feature_group_001: FeatureGroup.LISTING_INSIGHTS,
  });
}

export function trackAiDiagnosisEvent(action: string, listingId?: string): void {
  trackEvent({
    event: 'ubm_interaction',
    h_feature_group_001: 'ubm_listing_insights_hub_ai_diagnosis',
    h_event_action_007: action,
    h_event_label_008: undefined,
    ...(listingId && { h_listing_advertisement_id_120: listingId }),
  });
}

export function trackPackageOverviewInteraction({
  action,
  label,
}: {
  action: string;
  label?: string;
}): void {
  const eventParams: PageTrackingParameters = {
    event: 'ubm_interaction',
    h_event_action_007: action,
    ...(label !== undefined ? { h_event_label_008: label } : {}),
  };
  trackEvent(eventParams);
}

export const SELLER_LEADS_EVENT_LABEL = 'seller_leads';

export enum SellerLeadsTrackingAction {
  GO_TO_MANAGE_DISTRICTS = 'go_to_manage_districts',
  OPEN_ADD_DISTRICTS = 'open_add_districts',
  TRY_TO_SAVE_DISTRICTS = 'try_to_save_districts',
  CONFIRM_DISTRICTS = 'confirm_districts',
  SAVE_DISTRICTS = 'save_districts',
  RESAVE_DISTRICTS = 'resave_districts',
  DISPLAY_DISTRICTS_LIMITATION_ERROR = 'display_districts_limitation_error',
}

export function formatDistrictNamesForTracking(districtLabels: readonly string[]): string {
  return districtLabels.map((label) => label.toLowerCase()).join('&');
}

export function trackSellerLeadsInteraction(
  action: SellerLeadsTrackingAction,
  districtLabels?: readonly string[],
): void {
  trackEvent({
    event: 'ubm_interaction',
    h_event_action_007: action,
    h_event_label_008: districtLabels
      ? formatDistrictNamesForTracking(districtLabels)
      : SELLER_LEADS_EVENT_LABEL,
  });
}

export function trackSellerLeadsDistrictLimitationError(districtLabels: readonly string[]): void {
  trackEvent({
    event: 'feature_displayed',
    h_event_action_007: SellerLeadsTrackingAction.DISPLAY_DISTRICTS_LIMITATION_ERROR,
    h_event_label_008: formatDistrictNamesForTracking(districtLabels),
  });
}

export function trackInsightsHubInteraction({
  action,
  label,
  listingId,
}: {
  action: InsightsHubTrackingAction;
  label?: string;
  listingId?: string;
}): void {
  trackClickedElement({
    action,
    label,
    listingId,
    featureGroup: FeatureGroup.INSIGHTS_HUB,
  });
}

export function trackInsightsHubRankingInteraction({
  action,
  label,
  listingId,
}: {
  action: EventAction;
  label?: string;
  listingId?: string | null;
}): void {
  trackEvent({
    event: 'ubm_interaction',
    h_feature_group_001: FeatureGroup.INSIGHTS_HUB,
    h_event_action_007: action,
    h_event_label_008: label,
    ...(listingId !== undefined && listingId !== null
      ? { h_listing_advertisement_id_120: listingId }
      : {}),
  });
}

export function trackInsightsHubEditSearchQueryInteraction({
  action,
  platform,
  listingId,
  changedFilters,
}: {
  action: EventAction;
  platform: SupportedPlatforms;
  listingId?: string | null;
  changedFilters?: EditSearchQueryTrackingDimensions;
}): void {
  trackEvent({
    event: 'ubm_interaction',
    h_feature_group_001: FeatureGroup.INSIGHTS_HUB,
    h_event_action_007: action,
    h_event_label_008: mapRankingSnapshotPlatformToTrackingLabel(platform),
    ...(listingId !== undefined && listingId !== null
      ? { h_listing_advertisement_id_120: listingId }
      : {}),
    ...changedFilters,
  });
}

export function trackListingInsightsTabNavigation({
  fromTabId,
  toTabId,
  listingId,
}: {
  fromTabId: ListingInsightsTabId;
  toTabId: ListingInsightsTabId;
  listingId?: string;
}): void {
  const fromTabSlug = mapListingInsightsTabIdToTrackingSlug(fromTabId);
  const toTabSlug = mapListingInsightsTabIdToTrackingSlug(toTabId);

  trackInsightsHubInteraction({
    action: InsightsHubTrackingAction.NAVIGATE_TAB_INSIGHTS_HUB,
    label: buildTabNavigationLabel(fromTabSlug, toTabSlug),
    listingId,
  });
}

export function trackHelpCenterLinkClick(): void {
  trackEvent({
    event: 'ubm_interaction',
    h_event_action_007: 'open_help_center',
    h_event_label_008: undefined,
  });
}
